import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  bulkUploadContacts, getApiErrorMessage, getContacts, createContact, updateContact, deleteContact,
  getGroups, createGroup, deleteGroup, addContactsToGroup, removeContactsFromGroup,
  getTemplates, apiClient
} from '../../api-wa';
import AddContactModal, { parseSpreadsheetFile, validateBulkContactRows } from '../../Components/WhatsApp/AddContactModal';
import { useConfig } from '../../Context/WaConfigContext';

const groupChipPalette = ['#7c3aed', '#0891b2', '#c2410c', '#15803d', '#be123c', '#a16207', '#4338ca', '#0f766e'];
const colorForGroup = (id) => groupChipPalette[Number(id) % groupChipPalette.length];

const emptyForm = {
  name: '',
  phone_number: '',
  country_code: '+91',
  email: '',
  language: 'en_US',
};

const countryCodes = ['+91', '+1', '+44', '+61', '+971', '+65'];

const inputStyle = {
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-soft, #f8fafc)',
  color: 'var(--text-dark, #0f172a)',
  outline: 'none',
  fontSize: '14.5px',
  width: '100%',
  maxWidth: '100%',
  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
};

const actionStyle = {
  padding: '10px 14px',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  background: 'var(--brand-purple, #7c3aed)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '14px',
  boxShadow: 'var(--shadow-sm)', 
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

const fileLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px dashed rgba(0,0,0,0.18)',
  cursor: 'pointer',
  fontWeight: 700,
  background: 'rgba(15, 23, 42, 0.03)',
  color: '#16213f',
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '16px',
};

const modalCardStyle = {
  width: 'min(540px, 100%)',
  borderRadius: '24px',
  background: 'var(--surface-white, #ffffff)',
  border: '1px solid var(--glass-border, #e2e8f0)',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  padding: '32px',
  display: 'grid',
  gap: '20px',
};

const fieldLabelStyle = {
  fontSize: '14px',
  fontWeight: 700,
  color: 'var(--text-dark, #0f172a)',
  marginBottom: '6px',
};

const secondaryButtonStyle = {
  padding: '12px 16px',
  borderRadius: '12px',
  border: '1px solid var(--border-color, #e2e8f0)',
  background: 'var(--bg-main, #f1f5f9)',
  color: 'var(--text-dark, #0f172a)', 
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  fontWeight: 700,
  cursor: 'pointer',
};

const ContactsView = () => {
  const { activeConfigId, activeConfig, fetchConfigs } = useConfig();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('latest');
  const [form, setForm] = useState({ name: '', phone_number: '', email: '', country_code: '+91' });
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkTab, setBulkTab] = useState('file');
  const [pastedNumbers, setPastedNumbers] = useState('');
  const [pasteCountryCode, setPasteCountryCode] = useState('+91');
  const [pasteValidation, setPasteValidation] = useState({ valid: [], invalid: [] });
  const [pasting, setPasting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone_number: '', email: '', country_code: '+91' });
  
  // Groups
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupActionLoading, setGroupActionLoading] = useState(false);

  // Date-added range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Row selection
  const [selectedContactIds, setSelectedContactIds] = useState([]);

  // Auto-send template config states
  const [templates, setTemplates] = useState([]);
  const [autoSendEnabled, setAutoSendEnabled] = useState(false);
  const [autoSendTemplateName, setAutoSendTemplateName] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    if (activeConfig) {
      setAutoSendEnabled(Boolean(activeConfig.auto_send_template));
      setAutoSendTemplateName(activeConfig.auto_send_template_name || '');
    }
  }, [activeConfig]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const t = await getTemplates();
        setTemplates(t.filter(tpl => tpl.status === 'APPROVED'));
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
    if (activeConfigId) {
      loadTemplates();
    }
  }, [activeConfigId]);

  const handleSaveAutoSendSettings = async () => {
    if (autoSendEnabled && !autoSendTemplateName) {
      setError('Please select a template to auto-send, or disable automatic template notifications.');
      return;
    }
    setError('');
    try {
      setSubmitting(true);
      await apiClient.patch(`/whatsapp/config/${activeConfigId}/`, {
        auto_send_template: autoSendEnabled,
        auto_send_template_name: autoSendEnabled ? autoSendTemplateName : null,
      });
      await fetchConfigs();
      alert('Automatic template settings updated successfully!');
    } catch (saveError) {
      setError(getApiErrorMessage(saveError, 'Failed to save auto-send template settings.'));
    } finally {
      setSubmitting(false);
    }
  };

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = { page_size: 10000 };
      if (activeGroupId) params.group = activeGroupId;
      if (dateFrom) params.created_from = `${dateFrom}T00:00:00`;
      if (dateTo) params.created_to = `${dateTo}T23:59:59`;
      const response = await getContacts(params);
      const list = Array.isArray(response?.results) ? response.results : (Array.isArray(response) ? response : []);
      setContacts(list);
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, 'Unable to load contacts.'));
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await getGroups();
      setGroups(Array.isArray(response) ? response : []);
    } catch (loadError) {
      // Non-fatal group fetch error
    }
  };

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!activeConfigId) {
      setLoading(false);
      setContacts([]);
      return;
    }
    loadContacts();
    loadGroups();
    setCurrentPage(1);
    didMountRef.current = true;
  }, [activeConfigId]);

  useEffect(() => {
    if (!activeConfigId) return;
    if (!didMountRef.current) return; // Prevent duplicate request on initial mount
    loadContacts();
    setSelectedContactIds([]);
    setCurrentPage(1);
  }, [activeGroupId, dateFrom, dateTo]);

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = !term ? contacts : contacts.filter((contact) => {
      const name = String(contact.name || '').toLowerCase();
      const phone = String(contact.phone_number || '').toLowerCase();
      const email = String(contact.email || '').toLowerCase();
      return name.includes(term) || phone.includes(term) || email.includes(term);
    });

    return [...list].sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });
  }, [contacts, search, sortOrder]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Slice paginated contacts
  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredContacts.slice(startIndex, startIndex + pageSize);
  }, [filteredContacts, currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / pageSize));

  const handleToggleSelect = (contactId) => {
    setSelectedContactIds((prev) => (
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    ));
  };

  const handleToggleSelectAll = () => {
    if (selectedContactIds.length === paginatedContacts.length) {
      setSelectedContactIds([]);
    } else {
      setSelectedContactIds(paginatedContacts.map((c) => c.id));
    }
  };

  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      setCreatingGroup(true);
      setError('');
      await createGroup({ name });
      setNewGroupName('');
      setShowGroupModal(false);
      await loadGroups();
    } catch (createError) {
      setError(getApiErrorMessage(createError, 'Failed to create group.'));
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Delete this group? Contacts in it are not deleted, only the grouping.')) return;
    try {
      setGroupActionLoading(true);
      await deleteGroup(groupId);
      if (activeGroupId === groupId) setActiveGroupId(null);
      await loadGroups();
      await loadContacts();
    } catch (deleteErr) {
      setError(getApiErrorMessage(deleteErr, 'Failed to delete group.'));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleAssignSelectedToGroup = async (groupId) => {
    if (!groupId || selectedContactIds.length === 0) return;
    try {
      setGroupActionLoading(true);
      setError('');
      await addContactsToGroup(groupId, selectedContactIds);
      setSelectedContactIds([]);
      await loadGroups();
      await loadContacts();
    } catch (assignError) {
      setError(getApiErrorMessage(assignError, 'Failed to add contacts to group.'));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleRemoveSelectedFromGroup = async () => {
    if (!activeGroupId || selectedContactIds.length === 0) return;
    try {
      setGroupActionLoading(true);
      setError('');
      await removeContactsFromGroup(activeGroupId, selectedContactIds);
      setSelectedContactIds([]);
      await loadGroups();
      await loadContacts();
    } catch (removeError) {
      setError(getApiErrorMessage(removeError, 'Failed to remove contacts from group.'));
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleCreateContact = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.phone_number.trim()) {
      setError('Phone number is required.');
      return;
    }

    try {
      setSubmitting(true);
      await createContact({
        name: form.name.trim() || undefined,
        phone_number: `${form.country_code}${form.phone_number.trim()}`,
        email: form.email.trim() || undefined,
        language: 'en_US',
      });
      setForm(emptyForm);
      setShowAddModal(false);
      await loadContacts();
    } catch (createError) {
      setError(getApiErrorMessage(createError, 'Failed to create contact.'));
    } finally {
      setSubmitting(false);
    }
  };

  const attachUploadResultToActiveGroup = async (response) => {
    if (!activeGroupId) return;
    const createdIds = (response?.results || []).map((c) => c.id).filter(Boolean);
    const existingIds = response?.skipped_contact_ids || [];
    const allIds = [...createdIds, ...existingIds];
    if (allIds.length === 0) return;
    try {
      await addContactsToGroup(activeGroupId, allIds);
    } catch (groupError) {
      console.error(groupError);
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError('');
      setBulkFileName(file.name);
      const parsedRows = await parseSpreadsheetFile(file);
      const { cleanedRows, invalidRows } = validateBulkContactRows(parsedRows);

      if (cleanedRows.length === 0) {
        setError('The file did not contain any valid contact rows.');
        return;
      }

      if (invalidRows.length > 0) {
        const sampleErrors = invalidRows.slice(0, 3)
          .map((row) => `Row ${row.row}: ${row.errors.join(', ')}`)
          .join(' | ');
        setError(`Fix invalid rows before uploading. ${sampleErrors}`);
        return;
      }

      const response = await bulkUploadContacts(cleanedRows);
      await attachUploadResultToActiveGroup(response);
      setShowBulkModal(false);
      if (response?.skipped_existing) {
        setError(`Added ${response.created} contact${response.created === 1 ? '' : 's'}. Skipped ${response.skipped_existing} that already existed.`);
      }
      await loadContacts();
    } catch (uploadError) {
      setError(getApiErrorMessage(uploadError, 'Bulk upload failed.'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleValidatePaste = () => {
    const entries = pastedNumbers.split(/[\n,]+/).map((entry) => entry.trim()).filter(Boolean);
    const valid = [];
    const invalid = [];
    const seen = new Set();

    entries.forEach((entry) => {
      const digits = entry.replace(/\D/g, '');
      if (digits.length === 10 && !seen.has(digits)) {
        seen.add(digits);
        valid.push(digits);
      } else {
        invalid.push(entry);
      }
    });

    setPasteValidation({ valid, invalid });
  };

  const handleSubmitPastedNumbers = async () => {
    if (pasteValidation.valid.length === 0) return;

    try {
      setPasting(true);
      setError('');
      const cleanedRows = pasteValidation.valid.map((digits) => ({
        name: digits,
        phone_number: `${pasteCountryCode}${digits}`,
        email: '',
        language: 'en_US',
      }));

      const response = await bulkUploadContacts(cleanedRows);
      await attachUploadResultToActiveGroup(response);
      setShowBulkModal(false);
      setPastedNumbers('');
      setPasteValidation({ valid: [], invalid: [] });
      if (response?.skipped_existing) {
        setError(`Added ${response.created} contact${response.created === 1 ? '' : 's'}. Skipped ${response.skipped_existing} that already existed.`);
      }
      await loadContacts();
    } catch (pasteError) {
      setError(getApiErrorMessage(pasteError, 'Bulk upload failed.'));
    } finally {
      setPasting(false);
    }
  };

  const handleEditClick = (contact) => {
    setEditingContact(contact);
    const fullPhone = contact.phone_number || '';
    const phoneOnly = fullPhone.length >= 10 ? fullPhone.slice(-10) : fullPhone;
    let code = fullPhone.length > 10 ? fullPhone.slice(0, -10) : '+91';
    
    if (code === '91') code = '+91';
    if (!countryCodes.includes(code)) code = '+91';

    setEditForm({
      name: contact.name || '',
      phone_number: phoneOnly,
      country_code: code,
      email: contact.email || '',
      language: contact.language || 'en_US',
    });
    setShowEditModal(true);
  };

  const handleUpdateContact = async (event) => {
    event.preventDefault();
    setError('');

    if (!editForm.phone_number.trim()) {
      setError('Phone number is required.');
      return;
    }

    const cleanPhone = editForm.phone_number.trim().replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Phone number must be exactly 10 digits.');
      return;
    }

    try {
      setSubmitting(true);
      await updateContact(editingContact.id, {
        name: editForm.name.trim() || undefined,
        phone_number: `${editForm.country_code}${editForm.phone_number.trim()}`,
        email: editForm.email.trim() || undefined,
        language: editForm.language,
      });
      setShowEditModal(false);
      setEditingContact(null);
      await loadContacts();
    } catch (updateError) {
      setError(getApiErrorMessage(updateError, 'Failed to update contact.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    try {
      setDeleting(true);
      setError('');
      await deleteContact(contactId);
      await loadContacts();
    } catch (deleteError) {
      setError(getApiErrorMessage(deleteError, 'Failed to delete contact.'));
    } finally {
      setDeleting(false);
    }
  };

  const columnLabels = ['Name', 'Phone Number', 'Email', 'Groups', 'Actions'];

  const displayedRows = paginatedContacts.map((contact) => ({
    id: contact.id,
    ...contact,
    name: contact.name || '-',
    phone_number: contact.phone_number || '-',
    email: contact.email || '-',
  }));

  const allSelected = paginatedContacts.length > 0 && selectedContactIds.length === paginatedContacts.length;

  return (
    <div className="contacts-page" style={{ display: 'grid', gap: '20px' }}>
      {/* WhatsApp Auto-Send Settings Card */}
      {activeConfig && (
        <div style={{
          background: 'var(--surface-white, #ffffff)',
          borderRadius: '16px',
          border: '1px solid var(--border-color, #e2e8f0)',
          padding: '20px',
          boxShadow: 'var(--shadow-sm, 0 1px 2px 0 rgba(0,0,0,0.05))',
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', fontWeight: '700', color: 'var(--text-dark, #0f172a)' }}>
            WhatsApp Automatic Template Notification
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
            Automatically send a template message when a new contact is manually created, uploaded, or sends their first message.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark, #0f172a)' }}>
                Enable Automatic Send
              </label>
              <div className="toggle-switch" style={{ flexShrink: 0 }}>
                <input
                  type="checkbox"
                  id="auto_send_template"
                  name="auto_send_template"
                  checked={autoSendEnabled}
                  onChange={(e) => setAutoSendEnabled(e.target.checked)}
                />
                <label htmlFor="auto_send_template"></label>
              </div>
            </div>

            {autoSendEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-dark, #0f172a)' }}>
                  Select Template
                </label>
                <select
                  value={autoSendTemplateName}
                  onChange={(e) => setAutoSendTemplateName(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    background: 'var(--bg-main, #f8fafc)',
                    fontSize: '13.5px',
                    color: 'var(--text-dark, #0f172a)',
                    fontWeight: '600',
                    outline: 'none',
                    minWidth: '180px',
                  }}
                >
                  <option value="">-- Choose Template --</option>
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.name}>
                      {tpl.name} ({tpl.language})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              type="button"
              onClick={handleSaveAutoSendSettings}
              disabled={submitting}
              style={{
                ...actionStyle,
                padding: '8px 16px',
                fontSize: '13px',
                marginLeft: 'auto',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      )}

      {/* Groups row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <button
          type="button"
          onClick={() => setActiveGroupId(null)}
          style={{
            padding: '6px 14px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700,
            border: activeGroupId === null ? '1px solid transparent' : '1px solid var(--border-color)',
            background: activeGroupId === null ? 'var(--brand-purple)' : 'var(--bg-main)',
            color: activeGroupId === null ? '#fff' : 'var(--text-dark)',
            cursor: 'pointer',
          }}
        >
          All Contacts
        </button>
        {groups.map((group) => (
          <div
            key={group.id}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 8px 6px 14px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700,
              border: activeGroupId === group.id ? '1px solid transparent' : '1px solid var(--border-color)',
              background: activeGroupId === group.id ? colorForGroup(group.id) : 'var(--bg-main)',
              color: activeGroupId === group.id ? '#fff' : 'var(--text-dark)',
            }}
          >
            <span style={{ cursor: 'pointer' }} onClick={() => setActiveGroupId(group.id)}>
              {group.name} <span style={{ opacity: 0.75, fontWeight: 600 }}>({group.contact_count})</span>
            </span>
            <button
              type="button"
              title="Delete group"
              onClick={() => handleDeleteGroup(group.id)}
              disabled={groupActionLoading}
              style={{
                border: 'none', background: 'rgba(0,0,0,0.12)', color: 'inherit', cursor: 'pointer',
                borderRadius: '50%', width: '16px', height: '16px', fontSize: '11px', lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setShowGroupModal(true)}
          style={{
            padding: '6px 14px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 700,
            border: '1px dashed var(--border-color)', background: 'transparent', color: 'var(--text-gray)', cursor: 'pointer',
          }}
        >
          + New Group
        </button>
      </div>

      {/* Search and Action buttons in one line */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search contacts"
            style={{ ...inputStyle, width: '220px', fontSize: '13px', padding: '8px 10px', margin: 0 }}
          />
          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
            style={{ ...inputStyle, width: 'auto', fontSize: '13px', padding: '8px 10px', margin: 0 }}
          >
            <option value="latest">Sort: Latest First</option>
            <option value="oldest">Sort: Oldest First</option>
          </select>
          <span style={{ fontSize: '12px', color: 'var(--text-gray)' }}>Added</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            style={{ ...inputStyle, width: 'auto', fontSize: '13px', padding: '8px 10px', margin: 0 }}
          />
          <span style={{ fontSize: '12px', color: 'var(--text-gray)' }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            style={{ ...inputStyle, width: 'auto', fontSize: '13px', padding: '8px 10px', margin: 0 }}
          />
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              style={{ border: 'none', background: 'transparent', color: 'var(--text-gray)', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
            >
              Clear dates
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" onClick={() => setShowAddModal(true)} style={{ ...actionStyle, padding: '8px 12px', fontSize: '13px' }}>+ Add Contact</button>
          <button type="button" onClick={() => { setBulkTab('file'); setPastedNumbers(''); setPasteValidation({ valid: [], invalid: [] }); setShowBulkModal(true); }} style={{ ...secondaryButtonStyle, padding: '8px 12px', fontSize: '13px' }}>Bulk Upload</button>
        </div>
      </div>

      {/* Bulk selection action bar */}
      {selectedContactIds.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: '10px', background: 'var(--surface-soft, #f8fafc)',
          border: '1px solid var(--border-color)',
        }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-dark)' }}>
            {selectedContactIds.length} selected
          </span>
          <select
            defaultValue=""
            disabled={groupActionLoading || groups.length === 0}
            onChange={(event) => {
              const groupId = event.target.value;
              event.target.value = '';
              if (groupId) handleAssignSelectedToGroup(groupId);
            }}
            style={{ ...inputStyle, width: 'auto', fontSize: '13px', padding: '6px 10px', margin: 0 }}
          >
            <option value="" disabled>{groups.length === 0 ? 'Create a group first' : 'Add to group…'}</option>
            {groups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          {activeGroupId && (
            <button
              type="button"
              onClick={handleRemoveSelectedFromGroup}
              disabled={groupActionLoading}
              style={{ ...secondaryButtonStyle, padding: '6px 12px', fontSize: '12.5px' }}
            >
              Remove from this group
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedContactIds([])}
            style={{ border: 'none', background: 'transparent', color: 'var(--text-gray)', fontSize: '12.5px', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Clear selection
          </button>
        </div>
      )}

      {loading && <div style={{ fontSize: '13px' }}>Loading contacts...</div>}
      {!loading && error && <div style={{ color: '#b42318', padding: '8px', background: '#fee2e2', borderRadius: '6px', fontSize: '12px' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid var(--border-color)', background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', background: 'var(--surface-soft, #f8fafc)' }}>
                  <th style={{ padding: '10px', width: '32px' }}>
                    <input type="checkbox" checked={allSelected} onChange={handleToggleSelectAll} style={{ cursor: 'pointer' }} />
                  </th>
                  {columnLabels.map((label) => (
                    <th key={label} style={{ padding: '10px', fontWeight: 700, fontSize: '12px', color: 'var(--text-gray)' }}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.length > 0 ? displayedRows.map((contact) => (
                  <tr key={contact.id} style={{ borderTop: '1px solid var(--border-color)', background: 'transparent' }}>
                    <td style={{ padding: '12px 10px' }}>
                      <input
                        type="checkbox"
                        checked={selectedContactIds.includes(contact.id)}
                        onChange={() => handleToggleSelect(contact.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px 10px', fontSize: '13px', color: 'var(--text-dark)' }}>{contact.name}</td>
                    <td style={{ padding: '12px 10px', fontSize: '13px', color: 'var(--text-dark)' }}>{contact.phone_number}</td>
                    <td style={{ padding: '12px 10px', fontSize: '13px', color: 'var(--text-dark)' }}>{contact.email}</td>
                    <td style={{ padding: '12px 10px', fontSize: '13px' }}>
                      {Array.isArray(contact.groups) && contact.groups.length > 0 ? (
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {contact.groups.map((g) => (
                            <span
                              key={g.id}
                              style={{
                                fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
                                background: colorForGroup(g.id), color: '#fff',
                              }}
                            >
                              {g.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-muted, #94a3b8)', fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 10px', display: 'flex', gap: '10px', alignItems: 'center', fontSize: '13px' }}>
                      <button 
                        type="button" 
                        onClick={() => handleEditClick(contact)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer', 
                          fontSize: '12px',
                          color: '#3b82f6',
                          padding: '2px 4px',
                          fontWeight: 600,
                          textDecoration: 'underline'
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        type="button" 
                        onClick={() => handleDeleteContact(contact.id)}
                        disabled={deleting}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: deleting ? 'not-allowed' : 'pointer', 
                          fontSize: '12px',
                          color: '#ef4444',
                          padding: '2px 4px',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          opacity: deleting ? 0.5 : 1
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={columnLabels.length + 1} style={{ padding: '14px', textAlign: 'center', color: 'var(--text-muted, #94a3b8)', fontSize: '13px' }}>
                      No contacts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Toolbar */}
          {filteredContacts.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderRadius: '12px',
              border: '1px solid var(--border-color, #e2e8f0)',
              background: '#fff',
            }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary, #64748b)' }}>
                Showing <strong>{(currentPage - 1) * pageSize + 1}</strong> to <strong>{Math.min(currentPage * pageSize, filteredContacts.length)}</strong> of <strong>{filteredContacts.length}</strong> contacts
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '12.5px', color: 'var(--text-secondary, #64748b)' }}>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '8px',
                      border: '1px solid var(--border-color, #e2e8f0)',
                      fontSize: '12.5px',
                      outline: 'none',
                      background: 'var(--bg-main, #f8fafc)',
                      fontWeight: 600,
                    }}
                  >
                    {[10, 15, 25, 50, 100].map(sz => (
                      <option key={sz} value={sz}>{sz}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    style={{
                      ...secondaryButtonStyle,
                      padding: '6px 12px',
                      fontSize: '12.5px',
                      borderRadius: '8px',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    style={{
                      ...secondaryButtonStyle,
                      padding: '6px 12px',
                      fontSize: '12.5px',
                      borderRadius: '8px',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <AddContactModal visible={showAddModal} onClose={() => setShowAddModal(false)} onSaved={loadContacts} />

      {/* New Group Modal */}
      {showGroupModal && (
        <div style={modalOverlayStyle} onClick={() => setShowGroupModal(false)}>
          <div style={{ ...modalCardStyle, width: 'min(420px, 100%)' }} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong style={{ fontSize: '18px', color: 'var(--text-dark)' }}>New Group</strong>
              <button type="button" onClick={() => setShowGroupModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '24px', color: 'var(--text-gray)', lineHeight: 1 }}>×</button>
            </div>
            <input
              autoFocus
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleCreateGroup(); }}
              placeholder="e.g. Webinar Aug 15, Hot Leads, VIP"
              style={inputStyle}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={handleCreateGroup}
                disabled={creatingGroup || !newGroupName.trim()}
                style={{ ...actionStyle, opacity: (creatingGroup || !newGroupName.trim()) ? 0.6 : 1, cursor: (creatingGroup || !newGroupName.trim()) ? 'not-allowed' : 'pointer' }}
              >
                {creatingGroup ? 'Creating...' : 'Create Group'}
              </button>
              <button type="button" onClick={() => setShowGroupModal(false)} style={secondaryButtonStyle}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingContact && (
        <div style={modalOverlayStyle} onClick={() => { setShowEditModal(false); setEditingContact(null); }}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '20px', color: 'var(--text-dark)' }}>Edit Contact</strong>
              <button type="button" onClick={() => { setShowEditModal(false); setEditingContact(null); }} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '24px', color: 'var(--text-gray)', lineHeight: 1 }}>×</button>
            </div>
            {error && <div style={{ color: '#b42318', padding: '8px', background: '#fee2e2', borderRadius: '6px', fontSize: '12px' }}>{error}</div>}
            <form onSubmit={handleUpdateContact} style={{ display: 'grid', gap: '16px', marginTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr', alignItems: 'center', gap: '12px' }}>
                <label style={{ ...fieldLabelStyle, fontSize: '13px', marginBottom: 0 }}>Phone Number</label>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  background: 'var(--bg-main, #f1f5f9)', 
                  border: '1px solid var(--border-color, #e2e8f0)', 
                  borderRadius: '10px',
                  overflow: 'hidden',
                  maxWidth: '220px'
                }}>
                  <select 
                    value={editForm.country_code} 
                    onChange={(e) => setEditForm({ ...editForm, country_code: e.target.value })} 
                    style={{ 
                      border: 'none', 
                      background: 'transparent', 
                      padding: '8px 0 8px 10px', 
                      fontSize: '13px', 
                      fontWeight: '600', 
                      color: 'var(--brand-purple, #7c3aed)',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    {countryCodes.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                  <div style={{ width: '1px', height: '18px', background: 'rgba(0,0,0,0.1)', margin: '0 6px' }}></div>
                  <input 
                    type="tel" 
                    value={editForm.phone_number} 
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setEditForm({ ...editForm, phone_number: val });
                    }} 
                    style={{ 
                      border: 'none', 
                      background: 'transparent', 
                      padding: '8px 10px 8px 0', 
                      fontSize: '13px', 
                      flexGrow: 1, 
                      outline: 'none',
                      color: 'var(--text-dark)'
                    }} 
                    placeholder="10 digits"
                    required 
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: '12px' }}>
                <label style={{ ...fieldLabelStyle, fontSize: '13px', marginBottom: 0 }}>Contact Name</label>
                <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} style={{ ...inputStyle, marginTop: 0 }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: '12px' }}>
                <label style={{ ...fieldLabelStyle, fontSize: '13px', marginBottom: 0 }}>Email Address</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} style={{ ...inputStyle, marginTop: 0 }} placeholder="Email" />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '12px' }}>
                <button type="submit" disabled={submitting} style={{ ...actionStyle, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                  {submitting ? 'Updating...' : 'Update Contact'}
                </button>
                <button type="button" onClick={() => { setShowEditModal(false); setEditingContact(null); }} style={secondaryButtonStyle}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div style={modalOverlayStyle} onClick={() => setShowBulkModal(false)}>
          <div style={modalCardStyle} onClick={(event) => event.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong style={{ fontSize: '20px', color: 'var(--text-dark)' }}>Bulk Upload Contacts</strong>
                <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '2px' }}>Import contacts using CSV or Excel format</div>
              </div>
              <button type="button" onClick={() => setShowBulkModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '24px', color: 'var(--text-gray)', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setBulkTab('file')}
                style={bulkTab === 'file' ? { ...actionStyle, padding: '8px 12px', fontSize: '13px' } : { ...secondaryButtonStyle, padding: '8px 12px', fontSize: '13px' }}
              >
                Excel / CSV
              </button>
              <button
                type="button"
                onClick={() => setBulkTab('paste')}
                style={bulkTab === 'paste' ? { ...actionStyle, padding: '8px 12px', fontSize: '13px' } : { ...secondaryButtonStyle, padding: '8px 12px', fontSize: '13px' }}
              >
                Paste Numbers
              </button>
            </div>

            {bulkTab === 'file' ? (
              <>
                <p style={{ margin: '8px 0', color: 'var(--text-muted, #6b7280)', fontSize: '12px' }}>
                  Upload a CSV or Excel file. Required fields are name, phone number, and email.
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <label style={fileLabelStyle}>
                    {uploading ? 'Uploading...' : 'Choose CSV or Excel file'}
                    <input type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv" onChange={handleBulkUpload} style={{ display: 'none' }} />
                  </label>
                  <a
                    href="/sample-bulk-contacts.xlsx"
                    download="sample-bulk-contacts.xlsx"
                    style={{
                      ...fileLabelStyle,
                      textDecoration: 'none',
                      borderStyle: 'solid',
                      background: 'var(--bg-main, #f1f5f9)',
                    }}
                  >
                    Download example Excel
                  </a>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>
                  {bulkFileName ? `Last file: ${bulkFileName}` : 'No file uploaded yet.'}
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: '8px 0', color: 'var(--text-muted, #6b7280)', fontSize: '12px' }}>
                  Paste 10-digit numbers separated by commas or new lines. They'll be saved under the selected country code.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={pasteCountryCode}
                    onChange={(e) => setPasteCountryCode(e.target.value)}
                    style={{ ...inputStyle, width: '90px', flex: 'none' }}
                  >
                    {countryCodes.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>applied to every number below</span>
                </div>
                <textarea
                  value={pastedNumbers}
                  onChange={(e) => { setPastedNumbers(e.target.value); setPasteValidation({ valid: [], invalid: [] }); }}
                  placeholder={'9876543210, 9876543211\n9876543212'}
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted, #6b7280)' }}>
                    {pastedNumbers.split(/[\n,]+/).filter((v) => v.trim()).length} numbers entered
                  </span>
                  <button type="button" onClick={handleValidatePaste} style={{ ...secondaryButtonStyle, padding: '8px 12px', fontSize: '13px' }}>
                    Validate
                  </button>
                </div>
                {(pasteValidation.valid.length > 0 || pasteValidation.invalid.length > 0) && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div style={{ fontSize: '12px' }}>
                      <span style={{ color: '#16a34a', fontWeight: 700 }}>✓ {pasteValidation.valid.length} valid</span>
                      {pasteValidation.invalid.length > 0 && (
                        <span style={{ color: '#b42318', fontWeight: 700, marginLeft: '12px' }}>✗ {pasteValidation.invalid.length} invalid</span>
                      )}
                    </div>
                    {pasteValidation.valid.length > 0 && (
                      <button
                        type="button"
                        onClick={handleSubmitPastedNumbers}
                        disabled={pasting}
                        style={{ ...actionStyle, opacity: pasting ? 0.6 : 1, cursor: pasting ? 'not-allowed' : 'pointer' }}
                      >
                        {pasting ? 'Adding...' : `Add ${pasteValidation.valid.length} Contacts`}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsView;
